import React, { useMemo, useRef, useState } from 'react';
import { Dropdown, Tooltip } from 'antd';

import { FUNNEL } from '../icons';
import { useFilterByValue, useValueFacets } from './FilterByValueContext';
import { describeValueFilter } from '../../utils/filterValues';
import { valueFilterFor } from '../items/itemFilterValues';
import { valueMenuItems, useValueActionRunner } from '../items/CellValueMenu';
import { elementValueMenu } from '../items/valueFilterOptions';
import { elementLabel } from '../items/itemValueDisplay';
import useUnitSystem from '../../hooks/useUnitSystem';
import useAccountNames from '../../hooks/useAccountNames';
import { userIdsOf } from '../../utils/userElements';

/**
 * The read view's value actions (docs/platform-standards.md section 4, "Values
 * carry their actions"; KPR-1640, KPE-051 ruling i07): beside a value inside a
 * list, one small trigger - the `value-action` family, the same 20px round
 * muted glyph as the conversions and the help "?" - that opens the value's menu:
 * **Filter by this value**, **Exclude this value**, **Copy value**, each value
 * by name when the field holds several. It is the table cell's menu
 * (items/valueFilterOptions, the same model), so the pane and the table say
 * the same thing and write the same condition.
 *
 * It renders NOTHING outside a list (no FilterByValueProvider above it - the
 * public page, a modal) and nothing for a value that is not a facet (an empty
 * field, a long note). A click never selects the row behind it and never edits
 * anything: it narrows the list and leaves the record alone.
 */

const stop = event => event.stopPropagation();

export default function FilterValueTrigger({ element, value, resolveLookupValue, className = '' }) {
	const applyValueFilter = useFilterByValue();
	const facetFor = useValueFacets();
	const unitSystem = useUnitSystem();
	const run = useValueActionRunner({ apply: applyValueFilter });
	const [open, setOpen] = useState(false);
	const anchor = useRef(null);
	const userNames = useAccountNames(element?.dataType === 'user' ? userIdsOf(value) : [], open);

	const model = useMemo(
		() => (applyValueFilter ? elementValueMenu({ element, value, resolveLookupValue, facetFor, unitSystem, userNames }) : null),
		[applyValueFilter, element, value, resolveLookupValue, facetFor, unitSystem, userNames]
	);
	const items = useMemo(() => valueMenuItems(model), [model]);
	if (!model || !model.entries.length) return null;

	const label = `Value actions for ${elementLabel(element)}`;
	return (
		<span className={['value-action', className].filter(Boolean).join(' ')} onClick={stop} onKeyDown={stop} role="presentation">
			<Dropdown
				trigger={['click']}
				placement="bottomLeft"
				open={open}
				onOpenChange={setOpen}
				menu={{
					items,
					selectable: false,
					className: 'value-menu',
					onClick: ({ key }) => {
						setOpen(false);
						run(model, key, anchor.current?.closest('.item-pane__field')?.querySelector('.item-pane__value') || null);
					},
				}}
			>
				<span ref={anchor} className="value-action__anchor">
					<Tooltip title={open ? '' : 'Filter, exclude or copy'}>
						<button
							type="button"
							className="value-action__trigger filter-value__trigger"
							aria-label={label}
							aria-haspopup="menu"
							aria-expanded={open}
						>
							<FUNNEL />
						</button>
					</Tooltip>
				</span>
			</Dropdown>
		</span>
	);
}

/**
 * A value that IS the filter: the resolved title of a card lookup, rendered as
 * the door to "every record that points at this one". Falls back to plain text
 * wherever there is no list to narrow, so the same call site serves the pane,
 * the public page and the table.
 */
export function FilterValueLink({ element, value, resolveLookupValue, children, className = '' }) {
	const applyValueFilter = useFilterByValue();
	const facetFor = useValueFacets();
	const filter = applyValueFilter ? valueFilterFor({ element, value, resolveLookupValue, facetFor }) : null;
	if (!filter) return <>{children}</>;

	return (
		<button
			type="button"
			className={['filter-value__link', className].filter(Boolean).join(' ')}
			title={`Filter by ${describeValueFilter(filter)}`}
			onClick={event => { event.stopPropagation(); applyValueFilter(filter); }}
		>
			{children}
		</button>
	);
}
